// src/components/Wallet.tsx

import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Plus, Smartphone, Globe, Loader2, 
  Trash2, X, Check, Lock, ChevronRight, Coins, ArrowUpRight,
  Wallet as WalletIcon, History, AlertCircle
} from 'lucide-react';
import { doc, updateDoc, increment, addDoc, collection, serverTimestamp, arrayUnion, arrayRemove, onSnapshot, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { UserProfile } from '../../types';

interface WalletProps {
  profile: UserProfile;
}

// 🟢 PAYSTACK HOOK (Custom - No react-paystack dependency)
const usePaystack = () => {
  const [loaded, setLoaded] = useState(false);
  
  useEffect(() => {
    // Check if script already exists
    if (document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]')) {
      if (window.PaystackPop) setLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => {
      if (window.PaystackPop) setLoaded(true);
    };
    document.body.appendChild(script);
    // DO NOT REMOVE SCRIPT ON UNMOUNT
  }, []);
  
  return loaded;
};

declare global {
  interface Window {
    PaystackPop: any;
  }
}

export const Wallet: React.FC<WalletProps> = ({ profile }) => {
  const paystackLoaded = usePaystack();
  
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(profile?.cred || 0);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [savedCards, setSavedCards] = useState<any[]>(profile?.savedCards || []);
  
  // Payment Method Selection for Top Up
  const [selectedMethod, setSelectedMethod] = useState<'card' | 'apple' | 'payshap'>('card');
  
  // Card Management State
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [cardForm, setCardForm] = useState({ number: '', name: '', expiry: '', cvv: '' });
  
  // Toast/Alert State
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // 🔴 REPLACE WITH YOUR LIVE PUBLIC KEY
  const PAYSTACK_KEY = 'pk_live_7985b67599e69e640b5f64f870c93b8c77208617';

  const userId = profile?.uid;

  // ============================================
  // 🟢 REAL-TIME BALANCE & TRANSACTIONS
  // ============================================
  useEffect(() => {
    if (!userId) return;

    // Listen to balance changes
    const userRef = doc(db, 'users', userId);
    const unsubscribeUser = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setBalance(data.cred || data.credits || 0);
        setSavedCards(data.savedCards || []);
      }
    });

    // Fetch transactions
    const fetchTransactions = async () => {
      try {
        const txQuery = query(
          collection(db, 'users', userId, 'transactions'),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const txSnap = await getDocs(txQuery);
        const txData = txSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTransactions(txData);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        // Fallback: try without orderBy
        try {
          const txQuery = query(
            collection(db, 'users', userId, 'transactions'),
            limit(20)
          );
          const txSnap = await getDocs(txQuery);
          const txData = txSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          txData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setTransactions(txData);
        } catch (fallbackError) {
          console.error('Fallback fetch also failed:', fallbackError);
        }
      }
    };

    fetchTransactions();

    return () => unsubscribeUser();
  }, [userId]);

  // ============================================
  // 🟢 SHOW TOAST
  // ============================================
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ============================================
  // 🟢 ADD CARD
  // ============================================
  const handleAddCard = async () => {
    // Basic validation
    const cleanNumber = cardForm.number.replace(/\s/g, '');
    if (cleanNumber.length < 16) {
      showToast('Please enter a valid 16-digit card number', 'error');
      return;
    }
    if (!cardForm.expiry || !cardForm.expiry.match(/^\d{2}\/\d{2}$/)) {
      showToast('Please enter expiry as MM/YY', 'error');
      return;
    }
    if (!cardForm.cvv || cardForm.cvv.length < 3) {
      showToast('Please enter a valid CVV', 'error');
      return;
    }
    if (!cardForm.name.trim()) {
      showToast('Please enter the cardholder name', 'error');
      return;
    }

    setLoading(true);
    try {
      const last4 = cleanNumber.slice(-4);
      const brand = cleanNumber.startsWith('4') ? 'Visa' : 
                    cleanNumber.startsWith('5') ? 'Mastercard' :
                    cleanNumber.startsWith('3') ? 'Amex' : 'Card';
      
      const newCard = {
        id: `card_${Date.now()}`,
        last4,
        brand,
        expiry: cardForm.expiry,
        name: cardForm.name,
        token: 'tok_mock_' + Date.now(),
        createdAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'users', userId), {
        savedCards: arrayUnion(newCard)
      });

      setIsAddingCard(false);
      setCardForm({ number: '', name: '', expiry: '', cvv: '' });
      showToast(`✅ ${brand} ending in ${last4} added successfully!`, 'success');
    } catch (error: any) {
      console.error("Error saving card", error);
      showToast('Failed to save card: ' + error.message, 'error');
    }
    setLoading(false);
  };

  // ============================================
  // 🟢 DELETE CARD
  // ============================================
  const handleDeleteCard = async (e: React.MouseEvent, card: any) => {
    e.stopPropagation();
    if (!window.confirm(`Remove card ending in ${card.last4}?`)) return;
    
    try {
      await updateDoc(doc(db, 'users', userId), {
        savedCards: arrayRemove(card)
      });
      showToast(`✅ Card ending in ${card.last4} removed`, 'success');
    } catch(e: any) {
      console.error(e);
      showToast('Failed to remove card: ' + e.message, 'error');
    }
  };

  // ============================================
  // 🟢 HANDLE DEPOSIT WITH PAYSTACK
  // ============================================
  const handleDeposit = () => {
    const val = parseFloat(amount);
    
    // Validation
    if (!amount || isNaN(val) || val <= 0) {
      showToast('Please enter a valid positive amount', 'error');
      return;
    }
    if (val < 10) {
      showToast('Minimum purchase is R10', 'error');
      return;
    }
    if (val > 10000) {
      showToast('Maximum purchase is R10,000', 'error');
      return;
    }

    if (!paystackLoaded) {
      if (window.PaystackPop) {
        console.log('Paystack loaded but state says false - proceeding anyway');
      } else {
        showToast('Payment gateway is loading. Please wait...', 'error');
        return;
      }
    }

    setLoading(true);

    // Build payment channels based on selected method
    let channels = ['card'];
    if (selectedMethod === 'apple') {
      channels = ['mobile_money'];
    } else if (selectedMethod === 'payshap') {
      channels = ['bank_transfer', 'eft', 'ussd'];
    }

    const reference = `WALLET_${userId}_${Date.now()}_${Math.random().toString(36).substring(7)}`;

    try {
      const transactionOptions = {
        key: PAYSTACK_KEY,
        email: profile?.email || 'user@scoop.app',
        amount: Math.round(val * 100), // Convert to cents
        currency: 'ZAR',
        ref: reference,
        metadata: {
          walletTopUp: true,
          userId: userId,
          amount: val,
          customerName: profile?.displayName || 'Wallet User',
        },
        channels: channels,
        callback: async (response: any) => {
          console.log('Paystack callback:', response);
          
          if (response.status === 'success' || response.message === 'Approved') {
            try {
              const userRef = doc(db, 'users', userId);
              await updateDoc(userRef, { 
                cred: increment(val) 
              });

              // Add transaction record
              await addDoc(collection(db, 'users', userId, 'transactions'), {
                type: 'credit_purchase',
                amount: val,
                reference: response.reference || reference,
                status: 'completed',
                method: selectedMethod,
                createdAt: serverTimestamp()
              });

              showToast(`✅ Successfully added ${val} Credits!`, 'success');
              setAmount('');
            } catch (error) {
              console.error("Deposit fulfillment failed", error);
              showToast('Payment succeeded but update failed. Contact support with ref: ' + response.reference, 'error');
            }
          } else {
            showToast('Payment failed. Please try again.', 'error');
          }
          setLoading(false);
        },
        onClose: () => {
          console.log('Paystack modal closed');
          if (loading) {
            setLoading(false);
            showToast('Payment cancelled.', 'info');
          }
        }
      };

      if (!window.PaystackPop) {
        throw new Error('Paystack is not loaded. Please refresh and try again.');
      }

      const handler = window.PaystackPop.setup(transactionOptions);
      
      if (!handler) {
        throw new Error('Failed to initialize Paystack. Please try again.');
      }
      
      handler.openIframe();
      
    } catch (error: any) {
      console.error('Paystack error:', error);
      setLoading(false);
      showToast('Payment error: ' + error.message, 'error');
    }
  };

  // ============================================
  // 🟢 RENDER FUNCTIONS
  // ============================================
  
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  // Format expiry
  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  return (
    <div className="space-y-6">
      
      {/* 🟢 TOAST NOTIFICATION */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-2xl max-w-md animate-in slide-in-from-right duration-300 ${
          toast.type === 'success' ? 'bg-green-600 border border-green-500' :
          toast.type === 'error' ? 'bg-red-600 border border-red-500' :
          'bg-zinc-800 border border-zinc-700'
        } text-white`}>
          <div className="flex items-center gap-3">
            {toast.type === 'success' && <Check size={20} />}
            {toast.type === 'error' && <AlertCircle size={20} />}
            <span className="font-medium text-sm">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-auto text-white/50 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 🪙 CREDIT BALANCE CARD */}
      <div className="bg-gradient-to-br from-zinc-900 to-black p-8 rounded-2xl border border-white/10 relative overflow-hidden text-center">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="relative z-10">
            <p className="text-white/50 text-xs font-black uppercase tracking-widest mb-2 flex items-center justify-center gap-2">
                <Coins size={12} /> Available Credits
            </p>
            <h1 className="text-5xl font-black text-white">{balance || 0} CR</h1>
            <p className="text-white/30 text-xs mt-2">1 CR = R1</p>
        </div>
      </div>

      {/* 🏦 BLOCK 1: BUY CREDITS (Top Up) */}
      <div className="bg-zinc-900/50 p-6 rounded-2xl border border-white/5 animate-in slide-in-from-right">
          
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <WalletIcon size={20} /> Buy Credits
          </h3>

          {/* Amount Input */}
          <div className="relative mb-6">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white font-black text-xl">R</span>
              <input 
                  type="number"
                  min="10"
                  max="10000"
                  step="10"
                  value={amount}
                  onChange={(e) => {
                      if (e.target.value.includes('-')) return;
                      setAmount(e.target.value);
                  }}
                  placeholder="0.00"
                  className="w-full bg-black border border-white/10 rounded-xl p-4 pl-10 text-white text-2xl font-bold outline-none focus:border-white transition-colors"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 text-xs font-bold uppercase">
                  {amount && !isNaN(parseFloat(amount)) ? `= ${parseFloat(amount)} CR` : '0 CR'}
              </span>
          </div>

          {/* Quick Amount Buttons */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[50, 100, 200, 500].map((val) => (
              <button
                key={val}
                onClick={() => setAmount(val.toString())}
                className={`py-2 rounded-xl border text-xs font-bold transition-colors ${
                  parseFloat(amount) === val 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black border-white/10 text-white/50 hover:border-white/30'
                }`}
              >
                R{val}
              </button>
            ))}
          </div>

          {/* Payment Method Squares */}
          <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-3">Select Payment Method</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
              <button 
                onClick={() => setSelectedMethod('card')}
                className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all ${
                  selectedMethod === 'card' ? 'bg-white text-black border-white' : 'bg-black border-white/10 text-white/50 hover:bg-white/5'
                }`}
              >
                  <CreditCard size={24} />
                  <span className="text-[10px] font-bold uppercase">Card</span>
              </button>
              <button 
                onClick={() => setSelectedMethod('apple')}
                className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all ${
                  selectedMethod === 'apple' ? 'bg-white text-black border-white' : 'bg-black border-white/10 text-white/50 hover:bg-white/5'
                }`}
              >
                  <Smartphone size={24} />
                  <span className="text-[10px] font-bold uppercase">Apple Pay</span>
              </button>
              <button 
                onClick={() => setSelectedMethod('payshap')}
                className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all ${
                  selectedMethod === 'payshap' ? 'bg-white text-black border-white' : 'bg-black border-white/10 text-white/50 hover:bg-white/5'
                }`}
              >
                  <Globe size={24} />
                  <span className="text-[10px] font-bold uppercase">PayShap</span>
              </button>
          </div>

          {/* Pay Button */}
          <button 
              onClick={handleDeposit}
              disabled={loading || !amount || parseFloat(amount) < 10}
              className="w-full py-4 bg-white hover:bg-zinc-200 text-black font-black text-sm uppercase tracking-widest rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>Pay R{amount && !isNaN(parseFloat(amount)) ? parseFloat(amount).toFixed(2) : '0.00'}</>
              )}
          </button>
          
          <p className="text-[10px] text-white/30 text-center mt-3">
            <Lock size={12} className="inline mr-1" />
            Secured by Paystack
          </p>
      </div>

      {/* 💳 BLOCK 2: MANAGE CARDS */}
      <div className="bg-zinc-900/50 p-6 rounded-2xl border border-white/5">
          <div className="flex justify-between items-end mb-4">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <CreditCard size={20} /> My Cards
              </h3>
              <span className="text-white/30 text-xs">{savedCards?.length || 0} saved</span>
          </div>

          <div className="space-y-3">
              {/* Saved Cards List */}
              {savedCards && savedCards.length > 0 ? (
                  savedCards.map((card: any) => (
                      <div key={card.id} className="w-full bg-zinc-800 p-4 rounded-xl flex items-center justify-between border border-white/5 hover:border-white transition-all group">
                          <div className="flex items-center gap-4">
                              <div className="w-10 h-6 bg-white/10 rounded flex items-center justify-center text-white">
                                <CreditCard size={14}/>
                              </div>
                              <div className="text-left">
                                  <p className="text-white font-bold text-sm">•••• {card.last4}</p>
                                  <p className="text-white/30 text-[10px] uppercase">
                                    {card.brand} • {card.expiry || 'N/A'}
                                  </p>
                              </div>
                          </div>
                          <div className="flex items-center gap-3">
                              <button 
                                onClick={(e) => handleDeleteCard(e, card)} 
                                className="text-white/20 hover:text-red-500 p-2 transition-colors"
                                title="Remove card"
                              >
                                <Trash2 size={16}/>
                              </button>
                              <div className="w-5 h-5 rounded-full border border-white/20 group-hover:border-white group-hover:bg-white flex items-center justify-center">
                                  <Check size={12} className="text-black opacity-0 group-hover:opacity-100 transition-opacity"/>
                              </div>
                          </div>
                      </div>
                  ))
              ) : (
                  !isAddingCard && (
                    <div className="text-center text-white/30 text-xs py-6 border border-dashed border-white/10 rounded-xl">
                      No cards saved. Add one below.
                    </div>
                  )
              )}

              {/* Add Card Form OR Button */}
              {isAddingCard ? (
                  <div className="bg-black p-4 rounded-xl border border-white/10 animate-in fade-in">
                      <div className="flex justify-between items-center mb-4">
                          <h4 className="text-white font-bold text-sm">Enter Card Details</h4>
                          <button 
                            onClick={() => setIsAddingCard(false)} 
                            className="text-white/50 hover:text-white p-1"
                          >
                            <X size={16}/>
                          </button>
                      </div>
                      
                      <div className="space-y-3">
                          <div className="bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 flex items-center gap-3 focus-within:border-white transition-colors">
                              <CreditCard size={16} className="text-white/30 shrink-0"/>
                              <input 
                                  value={cardForm.number}
                                  onChange={e => setCardForm({...cardForm, number: e.target.value.replace(/\s/g, '')})}
                                  placeholder="Card Number"
                                  className="bg-transparent w-full text-white text-sm outline-none font-mono"
                                  maxLength={16}
                              />
                          </div>
                          
                          <div className="bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 focus-within:border-white transition-colors">
                              <input 
                                  value={cardForm.name}
                                  onChange={e => setCardForm({...cardForm, name: e.target.value})}
                                  placeholder="Cardholder Name"
                                  className="bg-transparent w-full text-white text-sm outline-none"
                              />
                          </div>
                          
                          <div className="flex gap-3">
                              <div className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 focus-within:border-white transition-colors">
                                  <input 
                                      value={cardForm.expiry}
                                      onChange={e => {
                                        const formatted = formatExpiry(e.target.value);
                                        setCardForm({...cardForm, expiry: formatted});
                                      }}
                                      placeholder="MM/YY"
                                      className="bg-transparent w-full text-white text-sm outline-none font-mono"
                                      maxLength={5}
                                  />
                              </div>
                              <div className="w-24 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 focus-within:border-white transition-colors">
                                  <input 
                                      value={cardForm.cvv}
                                      onChange={e => {
                                        const cleaned = e.target.value.replace(/\D/g, '');
                                        setCardForm({...cardForm, cvv: cleaned});
                                      }}
                                      placeholder="CVV"
                                      className="bg-transparent w-full text-white text-sm outline-none font-mono"
                                      maxLength={4}
                                      type="password"
                                  />
                              </div>
                          </div>
                          
                          <button 
                              onClick={handleAddCard}
                              disabled={loading}
                              className="w-full py-3 bg-white text-black font-bold text-sm rounded-lg hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                              {loading ? <Loader2 className="animate-spin" size={16}/> : 'Save Card'}
                          </button>
                      </div>
                  </div>
              ) : (
                  <button 
                      onClick={() => setIsAddingCard(true)}
                      className="w-full py-4 border border-dashed border-white/20 rounded-xl text-white/50 text-sm font-bold flex items-center justify-center gap-2 hover:bg-white/5 hover:text-white transition-colors"
                  >
                      <Plus size={18} /> Add New Card
                  </button>
              )}
          </div>
      </div>

      {/* 📜 TRANSACTION HISTORY */}
      <div>
          <h3 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-4 px-2 pt-4 flex items-center gap-2">
            <History size={14} /> Recent Activity
          </h3>
          <div className="space-y-2">
             {transactions.length > 0 ? (
                 transactions.slice(0, 5).map((t: any, i: number) => (
                     <div key={t.id || i} className="bg-zinc-900 border border-white/5 p-4 rounded-xl flex justify-between items-center hover:border-white/20 transition-colors">
                         <div className="flex items-center gap-3">
                             <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 text-white">
                                 {t.type === 'credit_purchase' ? <Plus size={18}/> : <ArrowUpRight size={18}/>}
                             </div>
                             <div>
                                 <p className="text-white font-bold text-sm capitalize">
                                   {t.type?.replace('_', ' ') || 'Transaction'}
                                 </p>
                                 <p className="text-white/30 text-[10px]">
                                   {t.createdAt?.seconds ? new Date(t.createdAt.seconds * 1000).toLocaleDateString('en-ZA') : 'Just now'}
                                 </p>
                               </div>
                         </div>
                         <span className="text-white font-black text-sm">
                           + {t.amount} CR
                         </span>
                     </div>
                 ))
             ) : (
                 <div className="text-center text-white/30 text-xs py-6 border border-dashed border-white/5 rounded-xl">
                   No recent transactions
                 </div>
             )}
          </div>
      </div>

    </div>
  );
};